import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Bell, ChevronRight, Download, Info, X } from 'lucide-react';
import TickerTape from '../components/layout/TickerTape';
// Discussion section hidden for now
// import GmpDiscussion from '../components/gmp/GmpDiscussion';
import OfferStrip from '../components/common/OfferStrip';
import ReviewStatCards from '../components/ipo-reviews/ReviewStatCards';
import RatingMethodologyGuide from '../components/ipo-reviews/RatingMethodologyGuide';
import ReviewControls from '../components/ipo-reviews/ReviewControls';
import ReviewsTable from '../components/ipo-reviews/ReviewsTable';
import ReviewsSidebar from '../components/ipo-reviews/ReviewsSidebar';
import ResearchVaultBanner from '../components/ipo-reviews/ResearchVaultBanner';
import {
  REVIEW_TABS,
  buildReviewsCsv,
  computeReviewStats,
  countByBucket,
  filterReviews,
  reviewTabByKey,
} from '../utils/ipoReviews';
import { fetchCatalogIndex, fetchCatalogReviews } from '../store/slices/ipoCatalogSlice';
import { catalogItemAndReviewsToReviewRow } from '../utils/ipoCatalogAdapters';

const NOTICE_MS = 4500;
const CATALOG_MAX_AGE_MS = 10 * 60 * 1000;
const EMPTY_FILTERS = { company: '', platform: 'all', minScore: '', analyst: '', consensus: 'all' };

// const DISCUSSION_COPY = {
//   headingId: 'reviews-discussion-heading',
//   title: 'Investor Discussions & Live Sentiment',
//   subtitle: 'Debate broker calls, CM scores and listing expectations with fellow investors.',
//   placeholder: 'Share your investment thesis, GMP observations, or ask questions about upcoming reviews...',
//   submitLabel: 'Post Comment',
//   startLabel: 'Start New Discussion',
// };

const REVIEW_YEAR = String(new Date().getFullYear());
const HEADING = `SME & Mainboard IPO Reviews (${REVIEW_YEAR})`;

export const IpoReviewsPage = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = reviewTabByKey(searchParams.get('tab')).key;
  const [verdict, setVerdict] = useState('all');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [notice, setNotice] = useState(null);
  const tableRef = useRef(null);

  const catalog = useSelector((state) => state.ipoCatalog.index.records);
  const indexStatus = useSelector((state) => state.ipoCatalog.index.status);
  const reviewsById = useSelector((state) => state.ipoCatalog.reviewsById);

  // The catalog list is one request and already carries everything the table
  // shows except the sector; the broker calls are then fetched per id, which
  // is all the backend offers for mainboard issues.
  useEffect(() => {
    (async () => {
      await dispatch(fetchCatalogIndex({ maxAgeMs: CATALOG_MAX_AGE_MS }));
      dispatch(fetchCatalogReviews());
    })();
  }, [dispatch]);

  // Real broker-review consensus (GET /api/core/ipos/{id}/broker-reviews) --
  // see utils/ipoCatalogAdapters.js for why cmScore/report/sector stay empty
  // (no backend source for any of them without a per-row detail request).
  const IPO_REVIEWS = useMemo(
    () => catalog.map((item) => catalogItemAndReviewsToReviewRow(item, reviewsById[item.id])),
    [catalog, reviewsById]
  );
  const STATS = useMemo(() => computeReviewStats(IPO_REVIEWS), [IPO_REVIEWS]);
  const REPORT_COUNT = useMemo(() => IPO_REVIEWS.filter((review) => review.report).length, [IPO_REVIEWS]);
  const TAB_COUNTS = useMemo(
    () => Object.fromEntries(REVIEW_TABS.map((t) => [t.key, IPO_REVIEWS.filter(t.test).length])),
    [IPO_REVIEWS]
  );

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${HEADING} | Alambrix IPO`;
    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    setPage(1);
  }, [tab, verdict, filters]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(null), NOTICE_MS);
    return () => clearTimeout(timer);
  }, [notice]);

  const verdictCounts = useMemo(
    () => countByBucket(IPO_REVIEWS.filter(reviewTabByKey(tab).test)),
    [IPO_REVIEWS, tab]
  );
  const rows = useMemo(
    () => filterReviews(IPO_REVIEWS, { tab, verdict, ...filters }),
    [IPO_REVIEWS, tab, verdict, filters]
  );

  const changeTab = (key) => setSearchParams(key === 'all' ? {} : { tab: key }, { replace: true });

  const changeFilter = (key, value) => setFilters((previous) => ({ ...previous, [key]: value }));

  const clearFilters = () => {
    setVerdict('all');
    setFilters(EMPTY_FILTERS);
  };

  const changePage = (nextPage) => {
    setPage(nextPage);
    // Paging from the bottom of a long table: bring its first row back into view
    if (tableRef.current && tableRef.current.getBoundingClientRect().top < 0) {
      tableRef.current.scrollIntoView({ block: 'start' });
    }
  };

  const handleExport = () => {
    // BOM so spreadsheet apps read the file as UTF-8
    const blob = new Blob(['﻿', buildReviewsCsv(rows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ipo-reviews-${tab}-${REVIEW_YEAR}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <>
      <TickerTape />

      <main className="flex-1 bg-slate-50/70">
        {/* Partner ad (GET /api/ads): reviews slot first, then the home leaderboard */}
        <OfferStrip slotId={['ipo-reviews-top', 'leaderboard']} />

        <div className="w-full max-w-[1680px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-28 pt-5 sm:pt-6 pb-12 sm:pb-16 space-y-5 sm:space-y-6">
          <div>
            <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-slate-500 font-medium overflow-x-auto whitespace-nowrap">
              <Link to="/" className="hover:text-[#008080] transition-colors">
                Home
              </Link>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <Link to="/ipo-dashboard" className="hover:text-[#008080] transition-colors">
                Primary Market Desk
              </Link>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span className="text-slate-900 font-semibold" aria-current="page">
                SME &amp; Mainboard IPO Reviews {REVIEW_YEAR}
              </span>
            </nav>

            <div className="mt-3 flex flex-col xl:flex-row xl:items-end justify-between gap-4">
              <div className="max-w-3xl">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight leading-tight">
                    {HEADING}
                  </h1>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-[11px] font-bold text-emerald-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" aria-hidden="true" />
                    Live Consensus Feed
                  </span>
                </div>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                  Broker research calls and consensus verdicts for current IPOs, consolidated from published review notes by
                  SEBI-registered research analysts and brokerages.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2 shrink-0">
                <label className="sr-only" htmlFor="reviews-year">
                  Review year
                </label>

                <button
                  type="button"
                  onClick={handleExport}
                  disabled={!rows.length}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-slate-200 bg-white text-xs font-bold text-slate-700 hover:border-slate-300 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors"
                >
                  <Download className="w-3.5 h-3.5" />
                  Export CSV
                </button>
                {/* <button
                  type="button"
                  onClick={() => setNotice('Research alerts are coming soon. New broker notes and CM ratings will appear on this page.')}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-teal-100 bg-teal-50 text-xs font-bold text-[#008080] hover:bg-teal-100 transition-colors"
                >
                  <Bell className="w-3.5 h-3.5" />
                  Research Alerts
                </button> */}
              </div>
            </div>
          </div>

          <ReviewStatCards stats={STATS} />

          <RatingMethodologyGuide />

          {/* Sidebar sits beside the table only from 1440px, where the table fits without scrolling.
              Narrower screens stack it between the table and the research vault. */}
          <div className="grid grid-cols-1 min-[1440px]:grid-cols-[minmax(0,1fr)_300px] gap-5 sm:gap-6 items-start">
            <div className="space-y-4 sm:space-y-5 min-w-0">
              <ReviewControls
                tab={tab}
                onTabChange={changeTab}
                tabCounts={TAB_COUNTS}
                verdict={verdict}
                onVerdictChange={setVerdict}
                verdictCounts={verdictCounts}
              />

              <ReviewsTable
                ref={tableRef}
                reviews={rows}
                page={page}
                onPageChange={changePage}
                filters={filters}
                onFilterChange={changeFilter}
                onOpenReport={(review) =>
                  setNotice(`The ${review.company} research note will be available once research reports are connected.`)
                }
                emptyMessage={
                  indexStatus === 'loading' ? (
                    'Loading the IPO catalog…'
                  ) : (
                    <>
                      No reviews match these filters.{' '}
                      <button type="button" onClick={clearFilters} className="font-bold text-[#008080] hover:underline">
                        Clear filters
                      </button>
                    </>
                  )
                }
              />
            </div>

            <ReviewsSidebar coverage={STATS.coverage} className="min-[1440px]:col-start-2 min-[1440px]:row-start-1 min-[1440px]:row-span-2" />

            <div className="space-y-4 sm:space-y-5 min-w-0">
              <ResearchVaultBanner
                reportCount={REPORT_COUNT}
                onOpen={() => setNotice('The research report vault is coming soon.')}
              />

              {/* Discussion section hidden for now: <GmpDiscussion initialThreads={[]} copy={DISCUSSION_COPY} /> */}

            </div>
          </div>
        </div>

        {notice && (
          <div
            role="status"
            className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md flex items-start gap-2 rounded-xl bg-slate-900 text-white text-xs px-4 py-3 shadow-elevated"
          >
            <Info className="w-4 h-4 flex-shrink-0 text-teal-300" />
            <span className="flex-1">{notice}</span>
            <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss" className="text-slate-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </main>
    </>
  );
};

export default IpoReviewsPage;
